import type { TrackInfo } from '@shared/types.ts';
import { player } from '@/lib/player.ts';
import { usePlayer } from '@/stores/playback.ts';
import { useNowPlaying } from '@/stores/nowPlaying.ts';
import { useUi } from '@/stores/ui.ts';
import {
  enterFullscreen, isFullscreenSupported,
  enterNativeVideoFullscreen, isNativeVideoFullscreenSupported,
} from '@/lib/fullscreen.ts';
import { secondaryLine, slotProgress } from '@/lib/slot.ts';
import { Focusable } from '@/ui/Focusable.tsx';
import { Transport } from '@/features/library/Transport.tsx';
import { PlayerOptions } from '@/features/library/PlayerOptions.tsx';
import { isTv } from '@/lib/native.ts';
import { ChannelLogo } from '@/ui/ChannelLogo.tsx';
import {
  IconPlay, IconPause, IconStop, IconRetry, IconExpand, IconCollapse,
} from '@/ui/icons.tsx';

/**
 * The player chrome. Presentation only — the media elements live in
 * PlayerHost and nothing here touches them.
 *
 * Over video this bar auto-hides; see .shell.idle in styles.css.
 */
export function MiniPlayer() {
  const { status, channel, item, error } = usePlayer();
  const track = useNowPlaying((s) => (channel ? s.tracks[channel.id] ?? null : null));
  const { view, setView, stepBack, zapping } = useUi();
  const tv = isTv();

  /*
   * A LIBRARY ITEM COUNTS AS SOMETHING PLAYING.
   *
   * This read `if (!channel) return null`, on the reasoning — written into
   * PlayerState — that everything already handling a channel would keep
   * working when items arrived. It did not: play a film, press BACK out of
   * theater, and the bar rendered nothing at all. There was no way to pause
   * it, stop it, or get back to it; the only exits were starting something
   * else or leaving the app.
   */
  if (!channel && !item) return null;

  // A film is always video. A channel may be radio.
  const isVideo = !channel || channel.kind === 'tv';
  const immersive = view !== 'browse';
  const secondary = channel ? secondaryLine(track, channel.kind) : null;
  const toggleLabel = status === 'error' ? 'Retry' : status === 'playing' ? 'Pause' : 'Play';
  /*
   * REAL FULLSCREEN, WHERE IT IS REAL — AND APPLE'S OWN, WHERE IT ISN'T.
   *
   * iOS (Safari and Chrome-for-iOS both — Chrome-iOS is WebKit underneath,
   * same as Safari, by Apple's own rule) has never supported the Fullscreen
   * API for anything but a native <video> — a custom player wrapper like
   * this one can call requestFullscreen() there and nothing will happen
   * except the browser chrome staying exactly where it was. Confirmed live:
   * the button's own state advanced correctly ("Exit full screen" did show),
   * the address bar simply never went anywhere, because it never could.
   *
   * iOS DOES support fullscreen on the <video> element itself, through the
   * older `webkitEnterFullscreen()` call — that hands the picture to Apple's
   * own full-screen player, not ours, which is why it gets no "Exit full
   * screen" button of its own below: Apple draws that UI, not this one.
   * Wherever the real Fullscreen API works, it's still preferred — it keeps
   * OUR controls (double-tap-to-seek, Stop) on screen, which Apple's player
   * doesn't offer room for.
   *
   * A button that cannot do EITHER of these is not a smaller feature, it is
   * a button that should not be drawn — especially on the one screen short
   * of room for every button that DOES.
   */
  const fsSupported = isFullscreenSupported();
  const nativeVideoFsSupported = !fsSupported
    && isNativeVideoFullscreenSupported(document.getElementById('n8m-video') as HTMLVideoElement | null);

  return (
    <div className="miniplayer" data-nav-zone="bottom">
      {immersive && (
        <Focusable id="mp-back" className="btn ghost" onSelect={() => stepBack()} aria-label="Back">
          {/* The chevron is already a picture, not a word — it is never
              hidden, only the word next to it is (see .btn-label, narrow
              viewport, styles.css). */}
          <span aria-hidden="true">‹</span><span className="btn-label"> Back</span>
        </Focusable>
      )}

      {/* While the remote is still cycling, the bar shows where you are
          HEADING, not what is still playing — otherwise holding right looks
          like nothing is happening at all. */}
      {channel
        ? <ChannelLogo channel={zapping ?? channel} />
        : item?.images.primary
          ? <img src={item.images.primary} alt="" className="mini-art" />
          : null}

      <div className="info">
        {/* The channel is the SECOND line here, not the first. What's actually
            on is what you want to read from the couch; the channel is how you
            got here and you already know it. For a film there is no such
            split — the title IS the thing — so it leads. */}
        <div className="chan">
          {zapping ? zapping.name
            : error ? <span className="error">{error}</span>
            : status === 'loading' ? (channel ? 'Tuning…' : 'Starting…')
            : item ? item.title
            : track ? track.title
            : status === 'paused' ? 'Paused' : 'Live'}
        </div>
        <div className="track">
          {zapping ? `Ch. ${zapping.number}`
            : channel ? channel.name
            : item?.year ? String(item.year)
              : status === 'paused' ? 'Paused' : 'Playing'}
          {!zapping && secondary && <span className="sep"> · {secondary}</span>}
        </div>
        {!zapping && channel && <SlotBar track={track} />}
      </div>

      {/* Skip, scrub and a position — renders nothing for a live channel,
          which has no timeline to move along. */}
      <Transport />

      {/* Audio track and speed. Also nothing for a channel: one of
          everything is not a choice. */}
      <PlayerOptions />

      <div className="controls">
        {/*
          THE PRESENTATION TIERS ARE A BROWSER IDEA, and on a television they
          are a lie.
          
          In a browser, browse / theater / fullscreen are three real sizes of
          the same video. On Android TV the picture comes from ExoPlayer on a
          surface behind the WebView, and it is already filling the screen —
          so "Expand" does nothing visible and "Exit full screen" does nothing
          either. Two buttons that cannot be wrong about anything except
          whether they work.
          
          A television has one state: it is playing, or it is not. So it gets
          the two controls that mean something.

          EACH ALSO CARRIES AN ICON NOW, for the same reason the toggle and
          Stop do below: on a phone the label is what got hidden, not the
          button, and the icon plus aria-label is what's left to press and to
          announce. See .btn-label in styles.css.
        */}
        {!tv && isVideo && view === 'browse' && (
          <Focusable id="mp-theater" className="btn" onSelect={() => setView('theater')} aria-label="Expand">
            <IconExpand size={18} /><span className="btn-label">Expand</span>
          </Focusable>
        )}

        {!tv && isVideo && (fsSupported || nativeVideoFsSupported) && view === 'theater' && (
          <Focusable
            id="mp-fullscreen"
            className="btn"
            aria-label="Full screen"
            onSelect={() => {
              // Synchronous, inside the gesture: both requestFullscreen()
              // and webkitEnterFullscreen() need a user gesture and the
              // context does not survive an await, so neither can be
              // deferred to an effect.
              if (fsSupported) {
                void enterFullscreen(useUi.getState().shell);
                setView('fullscreen');
              } else {
                // Apple's own player takes it from here; our view stays
                // 'theater' underneath — there is no tier of ours to step
                // into, and no exit button of ours to show (see above).
                enterNativeVideoFullscreen(document.getElementById('n8m-video') as HTMLVideoElement | null);
              }
            }}
          >
            <IconExpand size={18} /><span className="btn-label">Full screen</span>
          </Focusable>
        )}

        {!tv && isVideo && fsSupported && view === 'fullscreen' && (
          <Focusable id="mp-exitfs" className="btn" onSelect={() => setView('theater')} aria-label="Exit full screen">
            <IconCollapse size={18} /><span className="btn-label">Exit full screen</span>
          </Focusable>
        )}

        <Focusable
          id="mp-toggle"
          className="btn"
          aria-label={toggleLabel}
          onSelect={() => {
            if (status === 'error') player.retry();
            else if (status === 'playing') player.pause();
            else player.resume();
          }}
        >
          {status === 'error' ? <IconRetry size={18} />
            : status === 'playing' ? <IconPause size={18} />
              : <IconPlay size={18} />}
          <span className="btn-label">{toggleLabel}</span>
        </Focusable>

        <Focusable id="mp-stop" className="btn" onSelect={() => player.stop()} aria-label="Stop">
          <IconStop size={18} /><span className="btn-label">Stop</span>
        </Focusable>
      </div>
    </div>
  );
}

/**
 * Progress through the current programme or track.
 *
 * Driven entirely by a CSS animation whose duration is the time REMAINING,
 * started from the fraction already elapsed. React renders this once per
 * track change; the compositor does the rest. The alternative — a one-second
 * interval nudging a width — is a render a second for as long as the app is
 * open, on a device where the D-pad already feels the cost of a render pass.
 *
 * `key` is what restarts the animation: without it, a new track would inherit
 * the old element's in-flight animation.
 */
function SlotBar({ track }: { track: TrackInfo | null }) {
  const p = slotProgress(track);
  if (!p) return null;

  return (
    <div className="slotbar">
      <i
        key={track?.startedAtUtc ?? ''}
        style={{
          animationDuration: `${Math.round(p.remainingMs)}ms`,
          ['--from' as string]: p.fraction.toFixed(4),
        }}
      />
    </div>
  );
}
